// src/i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      dashboard: {
        title: "DairyKhataBook",
        admin: "Admin Dashboard",
        user: "User Dashboard",
        assoc: "Associated User",
        welcome: "Welcome back",
        welcome_name: "Welcome back, {{name}}",
        prompt: "Choose a task below to keep today's dairy records up to date.",
        logo_alt: "DairyKhataBook logo",
        loading: "Loading dashboard",
      },
      sections: {
        people: "People",
        operations: "Daily Operations",
        overview: "Overview",
        rates: "Milk Rate Setup",
      },
      actions: {
        customers: "Customers",
        active_today: "Active Today",
        milk_entry: "New Milk Entry",
        milk_subtitle: "Tap to add collection",
        ghee: "Record Ghee Sale",
        feed: "Record Cattle Feed",
        receive: "Money Received",
        pay: "Money Paid",
        view_entries: "View Entries",
        wallet: "Check Balance",
        cow_rate: "Cow Rates",
        buffalo_rate: "Buffalo Rates",
        check: "Check",
        manage: "Manage",
        primary: "Most used",
        view_subtitle: "Review records",
        wallet_subtitle: "See dairy or customer balance",
        rate_subtitle: "View or update rate table",
      },
      language: {
        change: "Change language",
        switch_to_english: "Switch language to English",
        switch_to_hindi: "Switch language to Hindi",
      },
      auth: {
        brand: "DairyKhataBook",
        login_meta_title: "Login - DairyKhataBook",
        register_meta_title: "Create Account - DairyKhataBook",
        login_title: "Sign in",
        login_subtitle: "Use a phone OTP, email sign-in link, or your password.",
        register_title: "Create your account",
        register_subtitle: "Register with a phone OTP, email sign-in link, or password.",
        password_login_title: "Password login",
        password_login_subtitle: "Use the mobile number and password linked to your account.",
        password_register_title: "Register with password",
        password_register_subtitle: "Create an account using your mobile number and a password.",
        method_label: "Choose sign-in method",
        phone_tab: "Phone OTP",
        email_tab: "Email link",
        password_tab: "Password",
        phone_label: "Mobile number",
        email_label: "Email address",
        phone_placeholder: "10-digit mobile number",
        email_placeholder: "name@example.com",
        invalid_phone: "Enter a valid 10-digit mobile number.",
        invalid_email: "Enter a valid email address.",
        phone_consent: "You may receive an SMS from Firebase. Standard message rates may apply.",
        send_code: "Send OTP",
        sending_code: "Sending OTP...",
        send_email_link: "Send sign-in link",
        sending_email_link: "Sending link...",
        email_link_sent_title: "Check your email",
        email_link_sent: "We sent a secure sign-in link to {{destination}}. Open it to continue.",
        email_link_confirm_help: "Enter the same email address that received this sign-in link.",
        complete_email_link: "Complete email sign-in",
        completing_email_link: "Completing sign-in...",
        change_email: "Change email",
        resend_email_link: "Send link again",
        code_sent: "We sent a 6-digit code to {{destination}}.",
        code_label: "Enter OTP",
        code_placeholder: "6-digit code",
        code_required: "Enter the 6-digit OTP.",
        code_invalid: "The OTP must contain 6 digits.",
        verify_code: "Verify and continue",
        verifying_code: "Checking OTP...",
        change_identifier: "Change mobile number",
        resend_code: "Send OTP again",
        resend_in: "Send again in {{seconds}}s",
        send_new_code: "Send new OTP",
        otp_expires_in: "OTP expires in {{time}}",
        otp_expired: "This OTP has expired. Send a new OTP.",
        request_error: "The OTP could not be sent. Please try again.",
        email_link_request_error: "The email sign-in link could not be sent. Please try again.",
        verify_error: "The OTP is invalid or expired. Please try again.",
        firebase_configuration_error: "Firebase login is not configured. Contact support.",
        firebase_invalid_code: "The SMS code is invalid or expired. Please try again.",
        firebase_session_invalid: "Your OTP session could not be verified. Send a new OTP and try again.",
        firebase_link_invalid: "This email sign-in link is invalid or expired. Request a new link.",
        firebase_too_many_requests: "Too many attempts. Please wait and try again.",
        firebase_unauthorized_domain: "This website is not authorized for Firebase login.",
        firebase_hostname_error: "Phone login is not allowed from {{hostname}}. Add {{hostname}} in Firebase Authentication > Settings > Authorized domains, then try again.",
        firebase_phone_disabled: "Phone sign-in is not enabled in Firebase Authentication.",
        firebase_network_error: "Firebase could not be reached. Check your internet connection and try again.",
        firebase_recaptcha_error: "The security check failed. Please try again.",
        recaptcha_label: "Firebase security check",
        otp_link: "Verify again",
        password_label: "Password",
        password_placeholder: "Enter your password",
        confirm_password_label: "Confirm password",
        confirm_password_placeholder: "Enter the password again",
        password_required: "Enter your password.",
        password_too_short: "Password must contain at least 6 characters.",
        passwords_mismatch: "The two passwords do not match.",
        show_password: "Show password",
        hide_password: "Hide password",
        legacy_submit: "Log in with password",
        legacy_submitting: "Logging in...",
        password_register_submit: "Create password account",
        password_register_submitting: "Creating account...",
        login_error: "Login failed. Check your mobile number and password.",
        register_error: "The account could not be created. Please try again.",
        google_separator: "or use Google",
        no_account: "New here?",
        create_account: "Create account",
        have_account: "Already have an account?",
        login_here: "Log in",
        back_home: "Back to home",
      },
      onboarding: {
        title: "Finish account setup",
        subtitle: "Your contact is verified. Tell us how you want to use the app.",
        session_expired: "Your verification session expired",
        language_label: "Choose language",
        english: "English",
        hindi: "हिंदी",
        name_label: "Your name",
        name_placeholder: "Enter your full name",
        name_required: "Enter your name.",
        role_label: "How will you use the app?",
        dairy_owner: "Dairy owner",
        dairy_owner_desc: "I collect milk and manage customer accounts.",
        farmer: "Farmer / supplier",
        farmer_desc: "I supply milk and want to see my account.",
        role_required: "Choose your account type.",
        terms_prefix: "I agree to the",
        privacy_policy: "Privacy Policy",
        terms_and: "and",
        terms_service: "Terms of Service",
        terms_required: "Accept the privacy policy and terms to continue.",
        submit: "Start using the app",
        submitting: "Setting up account...",
        complete_error: "Account setup could not be completed. Please try again.",
      },
      navigation: {
        label: "Main navigation",
        home: "Home",
        entry: "New entry",
        records: "Records",
        balance: "Balance",
      },
      feedback: {
        invite_title: "Help us improve",
        invite_body: "Tell us what feels easy and what could work better. It takes less than a minute.",
        open: "Share feedback",
        not_now: "Not now",
        title: "Share your feedback",
        close: "Close feedback dialog",
        question: "How was your experience?",
        select_rating: "Select a rating",
        rated: "You rated {{count}} out of 5",
        star_label: "Rate {{count}} out of 5",
        rating_error: "Please select a rating before submitting.",
        comments_label: "Feedback or suggestions",
        placeholder: "Tell us what would make DairyKhataBook easier to use...",
        cancel: "Cancel",
        submit: "Submit feedback",
        submitting: "Submitting...",
        success: "Thank you for your feedback.",
        error: "Feedback could not be submitted. Please try again.",
      },
      profile: {
        avatar_alt: "Your profile",
        open_menu: "Open profile menu",
        delete_account: "Delete account",
        logout: "Log out",
        confirm_title: "Delete your account?",
        confirm_body: "This permanently removes your account and all of its data. This action cannot be undone.",
        cancel: "Cancel",
        delete: "Delete account",
        delete_error: "Your account could not be deleted. Please try again.",
      },
      milkEntry: {
        headers: {
          calc: "Calculation",
          animal: "Animal",
          shift: "Shift",
          total: "Total Amount",
        },
        toggles: {
          fat: "Fat Only",
          fatSnf: "Fat & SNF",
          cow: "Cow",
          buffalo: "Buffalo",
          morning: "Morning",
          evening: "Evening",
        },
        form: {
          date: "Date",
          weight: "Milk (Ltr)",
          fat: "Fat (%)",
          snf: "SNF (%)",
          rate: "Rate (₹/Ltr)",
          auto_next: "Auto-next date",
          gen_receipt: "Generate Receipt",
          save_btn: "Save Entry",
        },
        receipt: {
          title: "MILK RECEIPT",
          time: "Time",
          user: "User",
          animal: "Animal",
          calc: "Calc",
          weight: "Weight",
          rate: "Rate",
          total: "TOTAL",
          generated: "Generated on",
        },
        messages: {
          submitting: "Submitting entry...",
          success: "Entry recorded! 👌",
          failed: "Submission failed 🤯",
          printer_error: "Receipt not ready",
          success_title: "Success!",
          record_msg: "Transaction recorded for",
          no_cust_title: "No Customer Selected",
          no_cust_msg: "Please select a customer from the list before saving.",
          share_wa: "Share on WhatsApp",
          print: "Print Receipt",
          dismiss: "Dismiss",
          okay: "Okay",
        },
        price_warning: {
          title: "Price Required",
          msg: "The Rate is 0. Please enter it manually or set standard market rates.",
          manual_btn: "Enter Manual Price",
          settings_btn: "Set Market Rates",
        },
      },
      defaultEntry: {
        options: {
          Feed: "Cattle Feed",
          Ghee: "Ghee",
          "Money Given": "Money Paid",
          "Money Received": "Money Received",
        },
        form: {
          date: "Date",
          type: "Transaction Type",
          amount: "Amount (₹)",
          quantity: "Quantity / Weight",
          price: "Price / Rate",
          total: "Grand Total",
          submit_btn: "Submit Entry",
        },
        alerts: {
          cust_title: "Select Customer",
          cust_msg: "Please select a customer before submitting.",
          close: "Close",
          success: "Entry added successfully",
          submitting: "Submitting...",
          failed: "Submission failed",
          share_fail: "Sharing failed",
        },
        receipt: {
          title: "TRANSACTION RECEIPT",
          options_title: "Receipt Options",
          print: "Print Receipt",
          share: "Share Receipt",
          processing: "Processing...",
          date: "Date",
          customer: "Customer",
          type: "Type",
          total: "TOTAL",
          thank_you: "Thank you 🙏",
        },
      },
      customers: {
        title: "Customers",
        add_btn: "Add Customer",
        search_placeholder: "Search name or number...",
        count_found: "{{count}} Customer Found",
        count_found_plural: "{{count}} Customers Found",
        no_number: "No number",
        view_profile: "View Profile",
        balance: "Bal: ₹{{amount}}",
        balance_label: "Balance",
        empty_title: "No customers found",
        empty_msg: "Try searching for a different name or number.",
        access_denied: "Access Denied.",
        loading: "Loading customers...",
        error: "Failed to load customers.",
      },
      arrived: {
        title: "Arrived Customers",
        labels: {
          date: "Arrival Date",
          shift: "Shift Selection",
          refresh: "Refresh List",
          syncing: "Syncing...",
        },
        shifts: {
          morning: "Morning",
          evening: "Evening",
        },
        stats: {
          showing: "Showing",
          arrivals_for: "arrivals for",
          update_alert: "Date changed - Update list",
        },
        card: {
          weight: "Milk",
          fat: "Fat",
          ltr: "Ltr",
        },
        empty: {
          title: "No arrivals found",
          msg: "We couldn't find any customers recorded for this date and shift.",
        },
      },
      viewEntries: {
        title: "View Milk Entries",
        labels: {
          select_user: "Select User",
          date_range: "Date Range",
          select_time: "Select Time",
        },
        toggles: {
          morning: "Morning",
          evening: "Evening",
        },
        empty_list: "No entries found for this period.",
      },
      card: {
        labels: {
          weight: "Milk",
          fat: "Fat",
          price: "Price",
          total: "Total",
          date: "Date",
          time: "Time",
          animal: "Animal Type",
          calc_type: "Calculation Type",
        },
        actions: {
          details: "Entry Details",
          print: "Print Receipt",
          close: "Close",
          delete: "Delete",
        },
        empty: "No Entries to show",
        ltr: "Ltr",
      },
      receipt: {
        title: "MILK RECEIPT",
        date: "Date",
        user: "User",
        animal: "Animal",
        weight: "Milk",
        fat: "Fat",
        snf: "SNF",
        rate: "Price/Rate",
        total: "TOTAL",
        generated: "Generated on",
        fat_only: "Fat Only",
        fat_snf: "Fat & SNF",
        ltr: "Ltr",
        receiptNotFound: "Receipt element not found",
        printNotSupported: "Printing not supported in this environment",
        generationFailed: "Failed to generate receipt image",
        popupBlocked: "Popup blocked. Please allow popups to print.",
      },
      balanceSheet: {
        title: "Account Entries",
        items: {
          milk_m: "Morning Milk",
          milk_e: "Evening Milk",
          feed: "Cattle Feed",
          ghee: "Ghee",
          money_given: "Money Paid",
          money_received: "Money Received",
          unknown: "Item",
        },
        summary: {
          net_diff: "For These Dates",
          prev_bal: "Previous Balance",
          final_total: "Total Balance",
        },
        details: {
          title: "Entry Details",
          item: "Type",
          date: "Date",
          quantity: "Quantity",
          name: "Name",
          amount: "Amount",
          not_available: "Not available",
        },
        actions: {
          delete: "Delete",
          close: "Close",
        },
        units: {
          ltr: "Ltr",
        },
      },
      balancePage: {
        title: "Balance",
        view_label: "Choose account",
        dairy_view: "My Dairy",
        customer_view: "Customer Account",
        date_label: "Choose Dates",
        sign_help: "+ adds to the balance · − reduces the balance",
        empty_title: "Select a customer",
        empty_body: "Choose a customer above to see their balance and entries.",
      },
      adminBalance: {
        error: "The balance could not be loaded. Please try again.",
        retry: "Try again",
        total_balance: "Total Balance",
        summary_line: "Previous: {{opening}} · These dates: {{period}}",
        previous_balance: "Previous Balance",
        selected_dates: "For These Dates",
        total_milk: "Total Milk",
        entry_count: "Entries: {{count}}",
        money_details: "Details for These Dates",
        all_entries: "All Entries",
        no_entries_title: "No entries found",
        no_entries_body: "Choose different dates to see older entries.",
        dairy: "Dairy",
        dairy_entry: "Dairy entry",
        customer_number: "Customer #{{id}}",
        weight_detail: "{{weight}} Ltr",
        milk_detail: "{{weight}} Ltr · Fat {{fat}}",
        quantity_detail: "{{quantity}} × ₹{{price}}",
        money_entry: "Money entry",
        money_in: "Money came in",
        money_out: "Money was paid",
        item_given: "Item given",
      },
      dateRange: {
        choose: "Choose dates",
        start: "From Date",
        end: "To Date",
        last_10_days: "Last 10 Days",
        one_month: "1 Month",
        three_months: "3 Months",
        six_months: "6 Months",
        all_time: "All Entries",
        apply: "Show Entries",
      },
      addUser: {
        title: "Add New Customer",
        labels: {
          mobile: "Mobile Number",
          name: "Full Name",
          password: "Set Password",
          hint: "Minimum 4 characters",
        },
        placeholders: {
          mobile: "e.g. 9876543210",
          name: "Enter full name",
          password: "Enter password",
        },
        buttons: {
          create: "Create User",
          creating: "Creating User...",
        },
        errors: {
          mobile_req: "Mobile number is required",
          mobile_valid: "Enter a valid 10-digit mobile number",
          mobile_invalid: "Invalid mobile number",
          name_req: "Full name is required",
          name_len: "Name must be at least 3 characters",
          name_chars: "Name should only contain letters and spaces",
          pass_req: "Password is required",
          pass_len: "Password must be at least 4 characters",
          img_type: "Only JPG or PNG images allowed",
          img_size: "Image size must be under 2MB",
        },
        toast: {
          success: "User added successfully!",
          generic_error: "An error occurred. Please try again.",
        },
      },
      userList: {
        label: "Select Customer",
        selectPrompt: "Select Customer...",
        searchPlaceholder: "Search customers by name or phone...",
        noCustomers: "No customers found",
        trySearch: "Try searching by phone number"
      },
      entrySetup: {
        empty_badge: "Customer setup",
        empty_title: "Add your first customer",
        empty_body: "Every entry needs a customer so balances, payments, and receipts stay accurate.",
        add_customer: "Add first customer",
        back_dashboard: "Back to dashboard",
        select_title: "Choose a customer to continue",
        select_body: "The entry form will open after you select a customer above.",
        load_error_title: "Customers couldn't be loaded",
        load_error_body: "Check your connection and try loading the customer list again.",
        retry: "Try again",
        dashboard_body: "Add a customer before recording milk or payment entries.",
      },
      report: {
        title: "Full Account",
        print_btn: "Print",
        headers: {
          name: "Name",
          mobile: "Mobile",
          period: "Dates",
        },
        sections: {
          milk: "Milk Entries",
          transactions: "Money and Items",
          summary: "Balance",
        },
        table: {
          date: "Date",
          session: "Session",
          weight: "Milk (Ltr)",
          fat: "Fat (%)",
          price: "Rate (₹/Ltr)",
          amount: "Amount",
          desc: "Description",
          quantity: "Qty",
          transaction: "Entry",
        },
        sessions: {
          morning: "Morning",
          evening: "Evening",
        },
        footer: {
          total_milk: "Total Milk",
          final_bal: "Total Balance",
        },
        desc_templates: {
          given_to: "Money Paid to {{name}}",
          given_generic: "Money Paid",
          rec_from: "Money Received from {{name}}",
          rec_generic: "Money Received",
          purchased_from: "{{item}} from {{name}}",
        },
        summary_rows: {
          prev_bal: "Previous Balance",
          milk_inc: "Milk Amount",
          feed_exp: "Cattle Feed",
          money_rec: "Money Received",
          money_giv: "Money Paid",
          ghee_exp: "Ghee",
        },
        empty_transactions: "No money or item entries found for these dates.",
        select_dates: "Choose dates to see the account.",
        error_prefix: "Could not load account",
      },
    },
  },
  hi: {
    translation: {
      dashboard: {
        title: "डेयरी खाता बुक",
        admin: "एडमिन डैशबोर्ड",
        user: "यूज़र डैशबोर्ड",
        assoc: "सहयोगी यूज़र",
        welcome: "फिर से स्वागत है",
        welcome_name: "फिर से स्वागत है, {{name}}",
        prompt: "आज का डेयरी हिसाब अपडेट रखने के लिए नीचे कोई काम चुनें।",
        logo_alt: "डेयरी खाता बुक लोगो",
        loading: "डैशबोर्ड लोड हो रहा है",
      },
      sections: {
        people: "ग्राहक और पार्टी",
        operations: "रोज़ाना के काम",
        overview: "कुल हिसाब",
        rates: "दूध रेट सेटिंग",
      },
      actions: {
        customers: "ग्राहक लिस्ट",
        active_today: "आज का कलेक्शन",
        milk_entry: "दूध जमा करें",
        milk_subtitle: "एंट्री करने के लिए दबाएं",
        ghee: "घी बेचें",
        feed: "पशु आहार",
        receive: "पैसे मिले",
        pay: "पैसे दिए",
        view_entries: "रजिस्टर देखें",
        wallet: "हिसाब देखें",
        cow_rate: "गाय का रेट",
        buffalo_rate: "भैंस का रेट",
        check: "चेक करें",
        manage: "मैनेज करें",
        primary: "सबसे ज़्यादा उपयोग",
        view_subtitle: "पुराना हिसाब देखें",
        wallet_subtitle: "डेयरी या ग्राहक का बाकी देखें",
        rate_subtitle: "रेट टेबल देखें या बदलें",
      },
      language: {
        change: "भाषा बदलें",
        switch_to_english: "भाषा अंग्रेज़ी में बदलें",
        switch_to_hindi: "भाषा हिंदी में बदलें",
      },
      auth: {
        brand: "डेयरी खाता बुक",
        login_meta_title: "लॉग इन - डेयरी खाता बुक",
        register_meta_title: "नया अकाउंट - डेयरी खाता बुक",
        login_title: "लॉग इन करें",
        login_subtitle: "फोन OTP, ईमेल साइन-इन लिंक या पासवर्ड से लॉग इन करें।",
        register_title: "नया अकाउंट बनाएं",
        register_subtitle: "फोन OTP, ईमेल साइन-इन लिंक या पासवर्ड से रजिस्टर करें।",
        password_login_title: "पासवर्ड से लॉग इन",
        password_login_subtitle: "अपने अकाउंट का मोबाइल नंबर और पासवर्ड डालें।",
        password_register_title: "पासवर्ड से रजिस्टर करें",
        password_register_subtitle: "मोबाइल नंबर और पासवर्ड से नया अकाउंट बनाएं।",
        method_label: "लॉग इन का तरीका चुनें",
        phone_tab: "फोन OTP",
        email_tab: "ईमेल लिंक",
        password_tab: "पासवर्ड",
        phone_label: "मोबाइल नंबर",
        email_label: "ईमेल पता",
        phone_placeholder: "10 अंकों का मोबाइल नंबर",
        email_placeholder: "name@example.com",
        invalid_phone: "सही 10 अंकों का मोबाइल नंबर डालें।",
        invalid_email: "सही ईमेल पता डालें।",
        phone_consent: "Firebase से SMS आ सकता है। सामान्य संदेश शुल्क लागू हो सकता है।",
        send_code: "OTP भेजें",
        sending_code: "OTP भेज रहे हैं...",
        send_email_link: "साइन-इन लिंक भेजें",
        sending_email_link: "लिंक भेजा जा रहा है...",
        email_link_sent_title: "अपना ईमेल देखें",
        email_link_sent: "हमने {{destination}} पर सुरक्षित साइन-इन लिंक भेजा है। आगे बढ़ने के लिए उसे खोलें।",
        email_link_confirm_help: "वही ईमेल पता डालें जिस पर यह साइन-इन लिंक आया था।",
        complete_email_link: "ईमेल साइन-इन पूरा करें",
        completing_email_link: "साइन-इन पूरा हो रहा है...",
        change_email: "ईमेल बदलें",
        resend_email_link: "लिंक दोबारा भेजें",
        code_sent: "{{destination}} पर 6 अंकों का OTP भेजा गया है।",
        code_label: "OTP डालें",
        code_placeholder: "6 अंकों का OTP",
        code_required: "6 अंकों का OTP डालें।",
        code_invalid: "OTP में 6 अंक होने चाहिए।",
        verify_code: "OTP जांचें और आगे बढ़ें",
        verifying_code: "OTP जांच रहे हैं...",
        change_identifier: "मोबाइल नंबर बदलें",
        resend_code: "OTP दोबारा भेजें",
        resend_in: "{{seconds}} सेकंड बाद दोबारा भेजें",
        send_new_code: "नया OTP भेजें",
        otp_expires_in: "OTP {{time}} में खत्म होगा",
        otp_expired: "इस OTP की समय सीमा खत्म हो गई है। नया OTP भेजें।",
        request_error: "OTP नहीं भेजा जा सका। दोबारा कोशिश करें।",
        email_link_request_error: "ईमेल साइन-इन लिंक नहीं भेजा जा सका। दोबारा कोशिश करें।",
        verify_error: "OTP गलत है या उसकी समय सीमा खत्म हो गई है।",
        firebase_configuration_error: "Firebase लॉगिन सेट नहीं है। सहायता से संपर्क करें।",
        firebase_invalid_code: "SMS कोड गलत है या उसकी समय सीमा खत्म हो गई है।",
        firebase_session_invalid: "OTP सत्र सत्यापित नहीं हो सका। नया OTP भेजकर दोबारा कोशिश करें।",
        firebase_link_invalid: "यह ईमेल साइन-इन लिंक गलत है या उसकी समय सीमा खत्म हो गई है। नया लिंक मंगाएं।",
        firebase_too_many_requests: "बहुत ज़्यादा कोशिशें हुई हैं। थोड़ा इंतज़ार करके दोबारा कोशिश करें।",
        firebase_unauthorized_domain: "यह वेबसाइट Firebase लॉगिन के लिए अधिकृत नहीं है।",
        firebase_hostname_error: "{{hostname}} से फोन लॉगिन की अनुमति नहीं है। Firebase Authentication > Settings > Authorized domains में {{hostname}} जोड़ें, फिर दोबारा कोशिश करें।",
        firebase_phone_disabled: "Firebase Authentication में फोन साइन-इन चालू नहीं है।",
        firebase_network_error: "Firebase से संपर्क नहीं हो सका। इंटरनेट कनेक्शन जांचकर दोबारा कोशिश करें।",
        firebase_recaptcha_error: "सुरक्षा जांच पूरी नहीं हुई। दोबारा कोशिश करें।",
        recaptcha_label: "Firebase सुरक्षा जांच",
        otp_link: "दोबारा OTP लें",
        password_label: "पासवर्ड",
        password_placeholder: "अपना पासवर्ड डालें",
        confirm_password_label: "पासवर्ड दोबारा डालें",
        confirm_password_placeholder: "वही पासवर्ड फिर से डालें",
        password_required: "पासवर्ड डालें।",
        password_too_short: "पासवर्ड कम से कम 6 अक्षर का होना चाहिए।",
        passwords_mismatch: "दोनों पासवर्ड एक जैसे नहीं हैं।",
        show_password: "पासवर्ड दिखाएं",
        hide_password: "पासवर्ड छिपाएं",
        legacy_submit: "पासवर्ड से लॉग इन करें",
        legacy_submitting: "लॉग इन हो रहा है...",
        password_register_submit: "पासवर्ड वाला अकाउंट बनाएं",
        password_register_submitting: "अकाउंट बन रहा है...",
        login_error: "लॉग इन नहीं हुआ। मोबाइल नंबर और पासवर्ड जांचें।",
        register_error: "अकाउंट नहीं बन सका। दोबारा कोशिश करें।",
        google_separator: "या Google से जारी रखें",
        no_account: "नया अकाउंट चाहिए?",
        create_account: "अकाउंट बनाएं",
        have_account: "पहले से अकाउंट है?",
        login_here: "लॉग इन करें",
        back_home: "होम पर वापस जाएं",
      },
      onboarding: {
        title: "अकाउंट की जानकारी पूरी करें",
        subtitle: "आपका नंबर या ईमेल सत्यापित हो गया है। अब अपनी जानकारी चुनें।",
        session_expired: "OTP की समय सीमा खत्म हो गई",
        language_label: "भाषा चुनें",
        english: "English",
        hindi: "हिंदी",
        name_label: "आपका नाम",
        name_placeholder: "अपना पूरा नाम लिखें",
        name_required: "अपना नाम लिखें।",
        role_label: "आप ऐप का उपयोग कैसे करेंगे?",
        dairy_owner: "डेयरी मालिक",
        dairy_owner_desc: "मैं दूध जमा करता हूं और ग्राहकों का हिसाब रखता हूं।",
        farmer: "किसान / दूध देने वाला",
        farmer_desc: "मैं दूध देता हूं और अपना हिसाब देखना चाहता हूं।",
        role_required: "अपना अकाउंट प्रकार चुनें।",
        terms_prefix: "मैं सहमत हूं:",
        privacy_policy: "गोपनीयता नीति",
        terms_and: "और",
        terms_service: "उपयोग की शर्तें",
        terms_required: "आगे बढ़ने के लिए नीति और शर्तें स्वीकार करें।",
        submit: "ऐप शुरू करें",
        submitting: "अकाउंट तैयार हो रहा है...",
        complete_error: "अकाउंट की जानकारी सेव नहीं हुई। दोबारा कोशिश करें।",
      },
      navigation: {
        label: "मुख्य नेविगेशन",
        home: "होम",
        entry: "नई एंट्री",
        records: "रजिस्टर",
        balance: "हिसाब",
      },
      feedback: {
        invite_title: "हमें बेहतर बनाने में मदद करें",
        invite_body: "बताएं क्या आसान लगा और क्या बेहतर हो सकता है। इसमें एक मिनट से भी कम लगेगा।",
        open: "फीडबैक दें",
        not_now: "अभी नहीं",
        title: "अपना फीडबैक दें",
        close: "फीडबैक विंडो बंद करें",
        question: "आपका अनुभव कैसा रहा?",
        select_rating: "रेटिंग चुनें",
        rated: "आपने 5 में से {{count}} रेटिंग दी",
        star_label: "5 में से {{count}} रेटिंग दें",
        rating_error: "सबमिट करने से पहले रेटिंग चुनें।",
        comments_label: "फीडबैक या सुझाव",
        placeholder: "बताएं डेयरी खाता बुक को और आसान कैसे बनाया जा सकता है...",
        cancel: "रद्द करें",
        submit: "फीडबैक भेजें",
        submitting: "भेज रहे हैं...",
        success: "फीडबैक के लिए धन्यवाद।",
        error: "फीडबैक नहीं भेजा जा सका। दोबारा कोशिश करें।",
      },
      profile: {
        avatar_alt: "आपकी प्रोफाइल",
        open_menu: "प्रोफाइल मेन्यू खोलें",
        delete_account: "अकाउंट डिलीट करें",
        logout: "लॉग आउट",
        confirm_title: "अपना अकाउंट डिलीट करें?",
        confirm_body: "इससे आपका अकाउंट और उसका सारा डेटा हमेशा के लिए हट जाएगा। इसे वापस नहीं किया जा सकता।",
        cancel: "रद्द करें",
        delete: "अकाउंट डिलीट करें",
        delete_error: "अकाउंट डिलीट नहीं हो सका। दोबारा कोशिश करें।",
      },
      milkEntry: {
        headers: {
          calc: "हिसाब का तरीका",
          animal: "पशु",
          shift: "समय / शिफ्ट",
          total: "कुल राशि",
        },
        toggles: {
          fat: "फैट (Fat)",
          fatSnf: "फैट और SNF",
          cow: "गाय",
          buffalo: "भैंस",
          morning: "सुबह",
          evening: "शाम",
        },
        form: {
          date: "तारीख",
          weight: "दूध (लीटर)",
          fat: "फैट (%)",
          snf: "SNF (%)",
          rate: "रेट / भाव (₹/लीटर)",
          auto_next: "अगली तारीख अपने आप",
          gen_receipt: "रसीद बनाएं",
          save_btn: "एंट्री सेव करें",
        },
        receipt: {
          title: "दूध की रसीद",
          date: "तारीख",
          user: "नाम",
          animal: "पशु",
          weight: "दूध",
          fat: "फैट",
          snf: "SNF",
          rate: "भाव",
          total: "कुल योग",
          generated: "समय",
          fat_only: "केवल फैट",
          fat_snf: "फैट और SNF",
          ltr: "लीटर",
          receiptNotFound: "रसीद तत्व नहीं मिला",
          printNotSupported: "इस डिवाइस में प्रिंटिंग समर्थित नहीं है",
          generationFailed: "रसीद इमेज बनाने में विफल",
          popupBlocked: "पॉपअप ब्लॉक हो गया। कृपया पॉपअप की अनुमति दें।",
        },
        messages: {
          submitting: "एंट्री हो रही है...",
          success: "एंट्री हो गई! 👌",
          failed: "एंट्री फेल हो गई 🤯",
          printer_error: "रसीद तैयार नहीं है",
          success_title: "सफल!",
          record_msg: "इनके नाम जमा हुआ:",
          no_cust_title: "ग्राहक नहीं चुना",
          no_cust_msg: "सेव करने से पहले कृपया लिस्ट से ग्राहक चुनें।",
          share_wa: "व्हाट्सएप पर भेजें",
          print: "रसीद प्रिंट करें",
          dismiss: "हटाएं",
          okay: "ठीक है",
        },
        price_warning: {
          title: "रेट (भाव) अनिवार्य है",
          msg: "रेट 0 है। कृपया इसे खुद लिखें या बाजार भाव सेटिंग्स में जाकर सेट करें।",
          manual_btn: "मैन्युअल रेट डालें",
          settings_btn: "बाजार भाव सेट करें",
        },
      },
      defaultEntry: {
        options: {
          Feed: "पशु आहार",
          Ghee: "देशी घी",
          "Money Given": "पैसे दिए",
          "Money Received": "पैसे मिले",
        },
        form: {
          date: "तारीख",
          type: "लेन-देन का प्रकार",
          amount: "रकम (₹)",
          quantity: "मात्रा / वज़न",
          price: "रेट / भाव",
          total: "कुल राशि",
          submit_btn: "एंट्री सेव करें",
        },
        alerts: {
          cust_title: "ग्राहक नहीं चुना",
          cust_msg: "सेव करने से पहले कृपया लिस्ट से ग्राहक चुनें।",
          close: "बंद करें",
          success: "एंट्री हो गई! 👌",
          submitting: "एंट्री हो रही है...",
          failed: "एंट्री फेल हो गई",
          share_fail: "शेयर नहीं हो पाया",
        },
        receipt: {
          title: "लेन-देन रसीद",
          options_title: "रसीद विकल्प",
          print: "प्रिंट करें",
          share: "शेयर करें",
          processing: "रुकिए...",
          date: "तारीख",
          customer: "ग्राहक",
          type: "विवरण",
          total: "कुल योग",
          thank_you: "धन्यवाद 🙏",
        },
      },
      customers: {
        title: "ग्राहक सूची",
        add_btn: "नया ग्राहक जोड़ें",
        search_placeholder: "नाम या नंबर खोजें...",
        count_found: "{{count}} ग्राहक मिला",
        count_found_plural: "{{count}} ग्राहक मिले",
        no_number: "नंबर नहीं है",
        view_profile: "प्रोफाइल देखें",
        balance: "बकाया: ₹{{amount}}",
        balance_label: "कुल बाकी",
        empty_title: "कोई ग्राहक नहीं मिला",
        empty_msg: "कृपया कोई और नाम या नंबर खोजें।",
        access_denied: "एक्सेस नहीं है।",
        loading: "ग्राहक लोड हो रहे हैं...",
        error: "डेटा लोड नहीं हो पाया।",
      },
      arrived: {
        title: "ग्राहक एंट्री रजिस्टर",
        labels: {
          date: "तारीख चुनें",
          shift: "शिफ्ट चुनें",
          refresh: "लिस्ट रिफ्रेश करें",
          syncing: "अपडेट हो रहा है...",
        },
        shifts: {
          morning: "सुबह (Morning)",
          evening: "शाम (Evening)",
        },
        stats: {
          showing: "कुल",
          arrivals_for: "एंट्री (शिफ्ट:",
          update_alert: "तारीख बदल गई है - अपडेट करें",
        },
        card: {
          weight: "दूध",
          fat: "फैट",
          ltr: "लीटर",
        },
        empty: {
          title: "कोई एंट्री नहीं मिली",
          msg: "इस तारीख और शिफ्ट के लिए कोई रिकॉर्ड नहीं है।",
        },
      },
      viewEntries: {
        title: "दूध का रजिस्टर",
        labels: {
          select_user: "ग्राहक चुनें",
          date_range: "तारीख चुनें",
          select_time: "समय चुनें",
        },
        toggles: {
          morning: "सुबह",
          evening: "शाम",
        },
        empty_list: "इस तारीख के लिए कोई एंट्री नहीं मिली।",
      },
      card: {
        labels: {
          weight: "दूध",
          fat: "फैट",
          price: "भाव (Rate)",
          total: "कुल राशि",
          date: "तारीख",
          time: "समय",
          animal: "पशु",
          calc_type: "हिसाब का तरीका",
        },
        actions: {
          details: "एंट्री का विवरण",
          print: "रसीद प्रिंट करें",
          close: "बंद करें",
          delete: "हटाएं",
        },
        empty: "कोई एंट्री नहीं है",
        ltr: "लीटर",
      },
      receipt: {
        title: "दूध की रसीद",
        date: "तारीख",
        user: "नाम",
        weight: "दूध",
        fat: "फैट",
        snf: "SNF",
        rate: "भाव",
        total: "कुल योग",
        generated: "समय",
        fat_only: "केवल फैट",
        fat_snf: "फैट और SNF",
        ltr: "लीटर",
      },
      balanceSheet: {
        title: "हिसाब की एंट्री",
        items: {
          milk_m: "सुबह का दूध",
          milk_e: "शाम का दूध",
          feed: "पशु आहार",
          ghee: "घी",
          money_given: "पैसे दिए",
          money_received: "पैसे मिले",
          unknown: "अन्य सामान",
        },
        summary: {
          net_diff: "इन तारीखों में",
          prev_bal: "पिछला बकाया",
          final_total: "कुल बाकी",
        },
        details: {
          title: "एंट्री का विवरण",
          item: "किस प्रकार",
          date: "तारीख",
          quantity: "मात्रा",
          name: "नाम",
          amount: "रकम",
          not_available: "जानकारी नहीं",
        },
        actions: {
          delete: "हटाएं",
          close: "बंद करें",
        },
        units: {
          ltr: "लीटर",
        },
      },
      balancePage: {
        title: "हिसाब-किताब",
        view_label: "हिसाब चुनें",
        dairy_view: "मेरी डेयरी",
        customer_view: "ग्राहक का हिसाब",
        date_label: "तारीख चुनें",
        sign_help: "+ बाकी बढ़ाता है · − बाकी घटाता है",
        empty_title: "ग्राहक चुनें",
        empty_body: "ऊपर ग्राहक चुनें, फिर उसका बाकी और सभी एंट्री देखें।",
      },
      adminBalance: {
        error: "हिसाब लोड नहीं हुआ। दोबारा कोशिश करें।",
        retry: "दोबारा कोशिश करें",
        total_balance: "कुल बाकी",
        summary_line: "पिछला बाकी: {{opening}} · इन तारीखों में: {{period}}",
        previous_balance: "पिछला बाकी",
        selected_dates: "इन तारीखों में",
        total_milk: "कुल दूध",
        entry_count: "कुल एंट्री: {{count}}",
        money_details: "इन तारीखों का पूरा हिसाब",
        all_entries: "सभी एंट्री",
        no_entries_title: "कोई एंट्री नहीं मिली",
        no_entries_body: "पुरानी एंट्री देखने के लिए दूसरी तारीख चुनें।",
        dairy: "डेयरी",
        dairy_entry: "डेयरी की एंट्री",
        customer_number: "ग्राहक #{{id}}",
        weight_detail: "{{weight}} लीटर",
        milk_detail: "{{weight}} लीटर · फैट {{fat}}",
        quantity_detail: "{{quantity}} × ₹{{price}}",
        money_entry: "रकम की एंट्री",
        money_in: "पैसे आए",
        money_out: "पैसे दिए",
        item_given: "सामान दिया",
      },
      dateRange: {
        choose: "तारीख चुनें",
        start: "शुरू तारीख",
        end: "अंतिम तारीख",
        last_10_days: "पिछले 10 दिन",
        one_month: "1 महीना",
        three_months: "3 महीने",
        six_months: "6 महीने",
        all_time: "सभी एंट्री",
        apply: "एंट्री दिखाएं",
      },
      addUser: {
        title: "नया ग्राहक जोड़ें",
        labels: {
          mobile: "मोबाइल नंबर",
          name: "पूरा नाम",
          password: "पासवर्ड सेट करें",
          hint: "कम से कम 4 अक्षर",
        },
        placeholders: {
          mobile: "जैसे 9876543210",
          name: "नाम लिखें",
          password: "आसान पासवर्ड लिखें",
        },
        buttons: {
          create: "ग्राहक बनाएं",
          creating: "प्रोसेस हो रहा है...",
        },
        errors: {
          mobile_req: "मोबाइल नंबर भरना ज़रूरी है",
          mobile_valid: "सही 10 अंकों का नंबर लिखें",
          mobile_invalid: "यह नंबर सही नहीं है",
          name_req: "नाम भरना ज़रूरी है",
          name_len: "नाम कम से कम 3 अक्षरों का होना चाहिए",
          name_chars: "नाम में केवल अक्षर होने चाहिए",
          pass_req: "पासवर्ड भरना ज़रूरी है",
          pass_len: "पासवर्ड कम से कम 4 अक्षरों का होना चाहिए",
          img_type: "केवल JPG या PNG फोटो मान्य है",
          img_size: "फोटो का साइज़ 2MB से कम होना चाहिए",
        },
        toast: {
          success: "नया ग्राहक जुड़ गया! 👌",
          generic_error: "कुछ गलती हुई। दोबारा प्रयास करें।",
        },
      },
      userList: {
        label: "ग्राहक चुनें",
        selectPrompt: "ग्राहक चुनें...",
        searchPlaceholder: "नाम या फोन से ग्राहक खोजें...",
        noCustomers: "कोई ग्राहक नहीं मिला",
        trySearch: "फोन नंबर से खोजने का प्रयास करें"
      },
      entrySetup: {
        empty_badge: "ग्राहक सेटअप",
        empty_title: "अपना पहला ग्राहक जोड़ें",
        empty_body: "हर एंट्री के लिए ग्राहक ज़रूरी है, ताकि बैलेंस, भुगतान और रसीद सही रहें।",
        add_customer: "पहला ग्राहक जोड़ें",
        back_dashboard: "डैशबोर्ड पर वापस जाएँ",
        select_title: "जारी रखने के लिए ग्राहक चुनें",
        select_body: "ऊपर ग्राहक चुनने के बाद एंट्री फॉर्म खुलेगा।",
        load_error_title: "ग्राहक लोड नहीं हो सके",
        load_error_body: "अपना इंटरनेट कनेक्शन जाँचें और ग्राहक सूची दोबारा लोड करें।",
        retry: "दोबारा प्रयास करें",
        dashboard_body: "दूध या भुगतान की एंट्री करने से पहले ग्राहक जोड़ें।",
      },
      report: {
        title: "पूरा हिसाब",
        print_btn: "प्रिंट करें",
        headers: {
          name: "नाम",
          mobile: "मोबाइल",
          period: "तारीखें",
        },
        sections: {
          milk: "दूध का विवरण",
          transactions: "रकम और सामान",
          summary: "बाकी रकम",
        },
        table: {
          date: "तारीख",
          session: "समय",
          weight: "दूध (लीटर)",
          fat: "फैट (%)",
          price: "रेट (₹/लीटर)",
          amount: "राशि (₹)",
          desc: "विवरण",
          quantity: "मात्रा",
          transaction: "एंट्री",
        },
        sessions: {
          morning: "सुबह",
          evening: "शाम",
        },
        footer: {
          total_milk: "कुल दूध",
          final_bal: "कुल बाकी",
        },
        desc_templates: {
          given_to: "{{name}} को पैसे दिए",
          given_generic: "पैसे दिए",
          rec_from: "{{name}} से पैसे मिले",
          rec_generic: "पैसे मिले",
          purchased_from: "{{name}} से {{item}} लिया",
        },
        summary_rows: {
          prev_bal: "पिछला बकाया",
          milk_inc: "दूध की रकम",
          feed_exp: "पशु आहार",
          money_rec: "पैसे मिले",
          money_giv: "पैसे दिए",
          ghee_exp: "घी",
        },
        empty_transactions: "इन तारीखों में रकम या सामान की कोई एंट्री नहीं है।",
        select_dates: "हिसाब देखने के लिए तारीख चुनें।",
        error_prefix: "हिसाब लोड नहीं हुआ",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
